import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utilities/AuthContext';
import { useSharedFunction } from '../utilities/SharedFunctionContext';

import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

const Navigation = () => {
  const navigate = useNavigate();
  const [auth, setAuth] = useAuth();
  const hasCheckedSession = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [forumOpen, setForumOpen] = useState(false);
  const { data } = useSharedFunction();

  const checkSession = async () => {
    if (hasCheckedSession.current) return;

    try {
      const response = await axios.get(`${EndPoint}/session`, {
        withCredentials: true,
      });
      setAuth({ ...auth, isLoggedIn: response.data.isLoggedIn });
    } catch (error) {
      console.error('Error checking session:', error);
      setAuth({ ...auth, isLoggedIn: false });
      navigate('/');
    }

    hasCheckedSession.current = true;
  };

  const handleLogout = async () => {
    try {
      await axios.post(
        `${EndPoint}/user/logout`,
        {},
        { withCredentials: true }
      );
      toast.success('You are logged out!', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
        hideProgressBar: false,
      });
      setAuth({ ...auth, isLoggedIn: false });
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    hasCheckedSession.current = false;
  }, [auth.isLoggedIn]);

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (data && auth.isLoggedIn) {
      setAuth((prevAuth) => ({
        ...prevAuth,
        credits: data,
      }));
    }
  }, [data, auth.isLoggedIn, setAuth]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!(e.target.closest && e.target.closest('[data-menu="tools"]'))) {
        setToolsOpen(false);
      }
      if (!(e.target.closest && e.target.closest('[data-menu="forum"]'))) {
        setForumOpen(false);
      }
      if (!(e.target.closest && e.target.closest('[data-menu="library"]'))) {
        setLibraryOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const navBtn =
    'text-white/90 px-4 py-2.5 rounded-2xl text-sm font-medium transition-all duration-200 hover:bg-white hover:text-slate-800 hover:shadow-sm';
  const navBtnMobile =
    'block w-full text-left text-white/90 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 hover:bg-white hover:text-slate-800';
  const dropdownItem =
    'block w-full rounded-xl px-4 py-2.5 text-left text-sm font-medium text-slate-700 transition-all duration-200 hover:bg-slate-100';
  const adminBtn =
    'text-white/90 px-4 py-2.5 rounded-2xl text-sm font-medium transition-all duration-200 hover:bg-white hover:text-slate-800 hover:shadow-sm';

  return (
    <nav className="sticky top-0 z-50 no-print border-b border-sky-800/80 bg-sky-900 shadow-lg shadow-sky-950/20">
      <ToastContainer />

      <div className="flex items-center justify-between px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-sky-500 text-lg font-bold text-white shadow-md">
            C
          </div>

          <div className="flex flex-col">
            <h1 className="text-white text-[34px] leading-none font-bold tracking-tight xs:hidden">
              Caredevo
            </h1>
            <span className="hidden sm:block text-[11px] uppercase tracking-[0.22em] text-sky-200/70">
              Clinical Workspace
            </span>
          </div>
        </div>

        {/* desktop */}
        <div className="hidden lg:flex xl:flex 2xl:flex items-center">
          {auth.isLoggedIn ? (
            <div className="flex items-center flex-wrap">
              <button
                onClick={() => (window.location.href = 'https://caredevo.com')}
                className={navBtn}
              >
                Home
              </button>

              <button
                onClick={() => navigate('/planning')}
                className="ml-1 rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-sky-900 shadow-sm transition-all duration-200 hover:bg-sky-50"
              >
                Care Plan
              </button>

              <button
                onClick={() => navigate('/account')}
                className={`${navBtn} ml-1`}
              >
                Account
              </button>

              {/* Library dropdown */}
              <div className="relative inline-block ml-1" data-menu="library">
                <button
                  onClick={() => setLibraryOpen((v) => !v)}
                  className={`${navBtn} flex items-center gap-2`}
                  aria-haspopup="true"
                  aria-expanded={libraryOpen}
                >
                  Library
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={`transition-transform duration-200 ${
                      libraryOpen ? 'rotate-180' : ''
                    }`}
                  >
                    <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
                  </svg>
                </button>

                {libraryOpen && (
                  <div className="absolute right-0 mt-3 w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl ring-1 ring-black/5 z-50">
                    <button
                      onClick={() => {
                        setLibraryOpen(false);
                        navigate('/prompt');
                      }}
                      className={dropdownItem}
                    >
                      Prompt
                    </button>

                    <button
                      onClick={() => {
                        setLibraryOpen(false);
                        navigate('/cdm-library');
                      }}
                      className={dropdownItem}
                    >
                      CDM
                    </button>
                  </div>
                )}
              </div>

              {/* admin buttons */}
              {auth && auth.role && auth.role.includes('admin') && (
                <button
                  onClick={() => navigate('/log')}
                  className={`${adminBtn} ml-1`}
                >
                  Logs
                </button>
              )}
              {auth && auth.role && auth.role.includes('admin') && (
                <button
                  onClick={() => navigate('/alluser')}
                  className={`${adminBtn} ml-1`}
                >
                  All User
                </button>
              )}
              {auth && auth.role && auth.role.includes('admin') && (
                <button
                  onClick={() => navigate('/agreement')}
                  className={`${adminBtn} ml-1`}
                >
                  Agreement
                </button>
              )}
              {auth && auth.role && auth.role.includes('admin') && (
                <button
                  onClick={() => navigate('/project')}
                  className={`${adminBtn} ml-1`}
                >
                  Project
                </button>
              )}
              {auth && auth.role && auth.role.includes('admin') && (
                <button
                  onClick={() => navigate('/lead')}
                  className={`${adminBtn} ml-1`}
                >
                  Leads
                </button>
              )}

              {auth && auth.role && auth.role.includes('admin') && (
                <button
                  onClick={() => navigate('/community')}
                  className={`${adminBtn} ml-1`}
                >
                  Discussion
                </button>
              )}

              {/* Forum dropdown */}
              <div className="relative inline-block ml-1" data-menu="forum">
                <button
                  onClick={() => setForumOpen((v) => !v)}
                  className={`${navBtn} flex items-center gap-2`}
                >
                  Forum
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={`transition-transform duration-200 ${
                      forumOpen ? 'rotate-180' : ''
                    }`}
                  >
                    <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
                  </svg>
                </button>

                {forumOpen && (
                  <div className="absolute right-0 mt-3 w-48 rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl ring-1 ring-black/5 z-50">
                    <button
                      onClick={() => {
                        setForumOpen(false);
                        navigate('/feedback');
                      }}
                      className={dropdownItem}
                    >
                      Feedback
                    </button>
                    <button
                      onClick={() => {
                        setForumOpen(false);
                        navigate('/roadmap');
                      }}
                      className={dropdownItem}
                    >
                      Roadmap
                    </button>
                  </div>
                )}
              </div>

              {/* Tools dropdown */}
              <div className="relative inline-block ml-1" data-menu="tools">
                <button
                  onClick={() => setToolsOpen((v) => !v)}
                  className={`${navBtn} flex items-center gap-2`}
                  aria-haspopup="true"
                  aria-expanded={toolsOpen}
                >
                  Tools
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={`transition-transform duration-200 ${
                      toolsOpen ? 'rotate-180' : ''
                    }`}
                  >
                    <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
                  </svg>
                </button>

                {toolsOpen && (
                  <div
                    className="absolute right-0 mt-3 w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl ring-1 ring-black/5 z-50"
                    role="menu"
                    aria-label="Tools"
                  >
                    <button
                      onClick={() => {
                        setToolsOpen(false);
                        navigate('/tools');
                      }}
                      className={dropdownItem}
                      role="menuitem"
                    >
                      Psychometric Tools
                    </button>
                    <button
                      onClick={() => {
                        setToolsOpen(false);
                        navigate('/opioid');
                      }}
                      className={dropdownItem}
                      role="menuitem"
                    >
                      Opioid Calculator
                    </button>
                  </div>
                )}
              </div>

              {auth?.credits !== undefined && auth?.credits !== null && (
                <div className="ml-3 rounded-2xl bg-sky-800/80 px-4 py-2 text-sm text-white shadow-inner ring-1 ring-white/10">
                  Credits:{' '}
                  <span className="font-semibold text-cyan-300">
                    {auth.credits}
                  </span>
                </div>
              )}

              <button
                onClick={handleLogout}
                className={`${navBtn} ml-2`}
              >
                Log out
              </button>
            </div>
          ) : (
            <div className="flex items-center">
              <button
                onClick={() => (window.location.href = 'https://caredevo.com')}
                className={navBtn}
              >
                Home
              </button>

              <button
                onClick={() => navigate('/login')}
                className={`${navBtn} ml-1`}
              >
                Login
              </button>
              <button
                onClick={() => navigate('/register')}
                className="ml-1 rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-sky-900 shadow-sm transition-all duration-200 hover:bg-sky-50"
              >
                Register
              </button>
            </div>
          )}
        </div>

        {/* mobile */}
        <div className="lg:hidden xl:hidden 2xl:hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="rounded-2xl border border-sky-700/70 bg-sky-800 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-white hover:text-slate-800"
          >
            {isOpen ? 'Close' : 'Menu'}
          </button>

          {isOpen && (
            <div className="absolute top-16 right-3 w-[260px] rounded-2xl border border-sky-800 bg-slate-900/95 p-4 shadow-2xl backdrop-blur-sm z-50">
              {auth.isLoggedIn ? (
                <>
                  <button
                    onClick={() => (window.location.href = 'https://caredevo.com')}
                    className={navBtnMobile}
                  >
                    Home
                  </button>

                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/feedback');
                    }}
                    className={navBtnMobile}
                  >
                    Feedback
                  </button>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/roadmap');
                    }}
                    className={navBtnMobile}
                  >
                    Roadmap
                  </button>

                  <div className="mt-3 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-200/60">
                    Tools
                  </div>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/tools');
                    }}
                    className={`${navBtnMobile} mt-1`}
                  >
                    Psychometric Tools
                  </button>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/opioid');
                    }}
                    className={navBtnMobile}
                  >
                    Opioid Calculator
                  </button>

                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/planning');
                    }}
                    className={`${navBtnMobile} mt-2`}
                  >
                    Care Plan
                  </button>

                  <div className="mt-3 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-200/60">
                    Library
                  </div>

                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/prompt');
                    }}
                    className={`${navBtnMobile} mt-1`}
                  >
                    Prompt
                  </button>

                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/library');
                    }}
                    className={navBtnMobile}
                  >
                    CDM
                  </button>

                  {auth?.credits !== undefined && auth?.credits !== null && (
                    <div className="mt-4 rounded-2xl bg-sky-800/70 px-4 py-3 text-sm text-white ring-1 ring-white/10">
                      Credits:{' '}
                      <span className="font-semibold text-cyan-300">
                        {auth.credits}
                      </span>
                    </div>
                  )}

                  {auth && auth.role && auth.role.includes('admin') && (
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        navigate('/log');
                      }}
                      className={`${navBtnMobile} mt-3`}
                    >
                      Logs
                    </button>
                  )}
                  {auth && auth.role && auth.role.includes('admin') && (
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        navigate('/alluser');
                      }}
                      className={navBtnMobile}
                    >
                      All User
                    </button>
                  )}

                  <button
                    onClick={handleLogout}
                    className="mt-4 block w-full rounded-2xl bg-white px-4 py-3 text-left text-sm font-semibold text-sky-900 transition-all duration-200 hover:bg-sky-50"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => (window.location.href = 'https://caredevo.com')}
                    className={navBtnMobile}
                  >
                    Home
                  </button>

                  <button
                    onClick={() => navigate('/login')}
                    className={navBtnMobile}
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="mt-2 block w-full rounded-2xl bg-white px-4 py-3 text-left text-sm font-semibold text-sky-900 transition-all duration-200 hover:bg-sky-50"
                  >
                    Register
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;